import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const GlobalContext = createContext();

const STORAGE_KEY = 't09n_global_context';

// Cache TTLs
const CACHE_TTL = {
  glossary: 5 * 60 * 1000,  // 5 minutes
  instructions: 10 * 60 * 1000,  // 10 minutes
  tm: 5 * 60 * 1000,  // 5 minutes
};

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobalContext must be used within GlobalContextProvider');
  }
  return context;
};

export const GlobalContextProvider = ({ children }) => {
  // Selection state
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedInstruction, setSelectedInstruction] = useState(null);
  const [selectedGlossary, setSelectedGlossary] = useState(null);
  const [selectedTM, setSelectedTM] = useState(null);
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [contextLoaded, setContextLoaded] = useState(false);
  
  // Modal state
  const [showContextModal, setShowContextModal] = useState(false);
  
  // Cache refs (in-memory cache)
  const cacheRef = useRef({
    glossary: { data: null, timestamp: 0, patterns: null },
    instructions: { data: null, timestamp: 0 },
    tm: { data: null, timestamp: 0 },
  });

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.project) setSelectedProject(parsed.project);
        if (parsed.instruction) setSelectedInstruction(parsed.instruction);
        if (parsed.glossary) setSelectedGlossary(parsed.glossary);
        if (parsed.tm) setSelectedTM(parsed.tm);
      }
    } catch (e) {
      console.error('Failed to load global context:', e);
    }
    setContextLoaded(true);
  }, []);

  // Track last saved IDs to prevent cross-tab loop: A saves → B storage event → B saves → A storage event → ...
  const lastSavedIdsRef = useRef({ project: null, instruction: null, glossary: null, tm: null });

  // Save to localStorage when selections change
  useEffect(() => {
    if (!contextLoaded) return;

    const ids = {
      project: selectedProject?.id ?? selectedProject?.project_id ?? null,
      instruction: selectedInstruction?.id ?? null,
      glossary: selectedGlossary?.id ?? null,
      tm: selectedTM?.id ?? null,
    };

    // Skip if IDs haven't changed — breaks the cross-tab localStorage loop
    const last = lastSavedIdsRef.current;
    if (last.project === ids.project && last.instruction === ids.instruction &&
        last.glossary === ids.glossary && last.tm === ids.tm) return;

    lastSavedIdsRef.current = ids;

    const data = {
      project: selectedProject,
      instruction: selectedInstruction,
      glossary: selectedGlossary,
      tm: selectedTM,
      timestamp: Date.now(),
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('Failed to save global context:', e);
    }
  }, [selectedProject, selectedInstruction, selectedGlossary, selectedTM, contextLoaded]);

  // Listen for cross-tab sync (storage event only fires in OTHER tabs, not the current one)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          // Use functional updates comparing IDs — prevents creating new object refs when ID unchanged
          setSelectedProject(prev =>
            (prev?.id ?? prev?.project_id) === (parsed.project?.id ?? parsed.project?.project_id)
              ? prev : (parsed.project || null));
          setSelectedInstruction(prev =>
            prev?.id === parsed.instruction?.id ? prev : (parsed.instruction || null));
          setSelectedGlossary(prev =>
            prev?.id === parsed.glossary?.id ? prev : (parsed.glossary || null));
          setSelectedTM(prev =>
            prev?.id === parsed.tm?.id ? prev : (parsed.tm || null));
        } catch (err) {
          console.error('Cross-tab sync error:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Pre-compile glossary regex patterns for fast matching
  const getCompiledGlossaryPatterns = useCallback(() => {
    const cache = cacheRef.current.glossary;
    if (cache.patterns && Date.now() - cache.timestamp < CACHE_TTL.glossary) {
      return cache.patterns;
    }
    return null;
  }, []);

  const setCompiledGlossaryPatterns = useCallback((patterns, glossaryData) => {
    cacheRef.current.glossary = {
      data: glossaryData,
      patterns: patterns,
      timestamp: Date.now(),
    };
  }, []);

  // Get cached instructions
  const getCachedInstructions = useCallback(() => {
    const cache = cacheRef.current.instructions;
    if (cache.data && Date.now() - cache.timestamp < CACHE_TTL.instructions) {
      return cache.data;
    }
    return null;
  }, []);

  const setCachedInstructions = useCallback((data) => {
    cacheRef.current.instructions = {
      data: data,
      timestamp: Date.now(),
    };
  }, []);

  // Get cached TM entries
  const getCachedTM = useCallback(() => {
    const cache = cacheRef.current.tm;
    if (cache.data && Date.now() - cache.timestamp < CACHE_TTL.tm) {
      return cache.data;
    }
    return null;
  }, []);

  const setCachedTM = useCallback((data) => {
    cacheRef.current.tm = {
      data: data,
      timestamp: Date.now(),
    };
  }, []);

  // Clear all context
  const clearContext = useCallback(() => {
    setSelectedProject(null);
    setSelectedInstruction(null);
    setSelectedGlossary(null);
    setSelectedTM(null);
    cacheRef.current = {
      glossary: { data: null, timestamp: 0, patterns: null },
      instructions: { data: null, timestamp: 0 },
      tm: { data: null, timestamp: 0 },
    };
  }, []);

  // Quick select project (also selects its default instruction/glossary)
  const selectProject = useCallback((project) => {
    setSelectedProject(project);
    
    // Auto-select project's instruction if available
    if (project?.instruction_id) {
      setSelectedInstruction({
        id: project.instruction_id,
        name: project.instruction_name || 'Project Instructions',
      });
    }
    
    // Auto-select project's glossary if available
    if (project?.glossary_id) {
      setSelectedGlossary({
        id: project.glossary_id,
        name: project.glossary_name || 'Project Glossary',
      });
    }
    
    // Auto-select project's TM if available
    if (project?.tm_id) {
      setSelectedTM({
        id: project.tm_id,
        name: project.tm_name || 'Project TM',
      });
    }
  }, []);

  // Check if any context is selected
  const hasContext = selectedProject || selectedInstruction || selectedGlossary || selectedTM;

  // Get context summary for display
  const getContextSummary = useCallback(() => {
    const parts = [];
    if (selectedProject) parts.push(`Project: ${selectedProject.name}`);
    if (selectedInstruction) parts.push(`Instructions: ${selectedInstruction.name}`);
    if (selectedGlossary) parts.push(`Glossary: ${selectedGlossary.name}`);
    if (selectedTM) parts.push(`TM: ${selectedTM.name}`);
    return parts.join(' • ');
  }, [selectedProject, selectedInstruction, selectedGlossary, selectedTM]);

  // Get context IDs for API calls
  const getContextIds = useCallback(() => {
    return {
      projectId: selectedProject?.id || selectedProject?.project_id || null,
      instructionId: selectedInstruction?.id || null,
      glossaryId: selectedGlossary?.id || null,
      tmId: selectedTM?.id || null,
    };
  }, [selectedProject, selectedInstruction, selectedGlossary, selectedTM]);

  return (
    <GlobalContext.Provider
      value={{
        // Selections
        selectedProject,
        selectedInstruction,
        selectedGlossary,
        selectedTM,
        
        // Setters
        setSelectedProject,
        setSelectedInstruction,
        setSelectedGlossary,
        setSelectedTM,
        selectProject,
        clearContext,
        
        // Modal
        showContextModal,
        setShowContextModal,
        
        // State
        loading,
        setLoading,
        hasContext,
        contextLoaded,
        
        // Cache utilities
        getCompiledGlossaryPatterns,
        setCompiledGlossaryPatterns,
        getCachedInstructions,
        setCachedInstructions,
        getCachedTM,
        setCachedTM,
        
        // Helpers
        getContextSummary,
        getContextIds,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export default GlobalContext;
